import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import StripePayment from './StripePayment';
import { useGroupStore } from '../hooks/useGroupStore';
import api from '../utils/api';
import { getCurrentUser } from '../utils/auth';

import InfoModal from '../components/common/InfoModal';
import SupplierInfoModal from '../components/common/SupplierInfoModal';

const EPSILON = 0.001;

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

function PortionSelector({ group, portion, onChange }) {
  const getMultiplier = (p) => (p === 'Half' ? 0.5 : p === 'Quarter' ? 0.25 : 1);
  const costPerFull = group.costPerPortion ? Number(group.costPerPortion) : 0;
  const rem = Number(group.remainingCost ?? 0);
  const options = group.portionsAvailable || ['Full', 'Half', 'Quarter'];

  return (
    <select value={portion} onChange={(e) => onChange(e.target.value)} className="border p-2 rounded w-full mb-4">
      <option value="">-- Select Portion --</option>
      {options.map((opt) => {
        const disabled = costPerFull * getMultiplier(opt) > rem;
        return (
          <option key={opt} value={opt} disabled={disabled}>
            {opt} {disabled ? '(Unavailable)' : ''}
          </option>
        );
      })}
    </select>
  );
}

export default function SearchAndJoinGroup() {
  const { groups: searchResults, fetchGroups, replaceGroup, removeGroup } = useGroupStore();
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [portion, setPortion] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [fadingGroupId, setFadingGroupId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [modalInfo, setModalInfo] = useState({ isOpen: false, title: '', content: null });
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierForModal, setSupplierForModal] = useState(null);

  const currentUser = getCurrentUser();
  const query = useQuery();

  // Scoped user: supplieradmin or member
  const scopedUser =
    currentUser &&
    ['supplieradmin', 'member'].includes((currentUser.role || '').toLowerCase()) &&
    currentUser.supplierId;

  const showModal = (title, content) => setModalInfo({ isOpen: true, title, content });
  const closeModal = () => setModalInfo({ ...modalInfo, isOpen: false });

  const openSupplierModal = (supplier) => {
    if (!supplier) return;
    setSupplierForModal(supplier);
    setSupplierModalOpen(true);
  };
  const closeSupplierModal = () => {
    setSupplierModalOpen(false);
    setSupplierForModal(null);
  };

  // Initialize suppliers
  useEffect(() => {
    const initSuppliers = async () => {
      try {
        const role = (currentUser?.role || '').toLowerCase();
        const isScopedSupplierAdmin = ['supplieradmin', 'member'].includes(role) || (role === 'admin' && currentUser?.supplierId);

        if (isScopedSupplierAdmin) {
          const res = await api.get('/api/suppliers/my-suppliers');
          const list = Array.isArray(res.data) ? res.data : [];
          if (list.length > 0) {
            setSuppliers(list);
            const qSupplierId = query.get('supplierId');
            setSupplierId(qSupplierId && list.find((s) => s._id === qSupplierId) ? qSupplierId : list[0]._id);
          } else {
            setSuppliers([]);
            setSupplierId('');
          }
        } else if (role === 'superadmin') {
          const res = await api.get('/api/suppliers');
          const list = Array.isArray(res.data) ? res.data : res.data.suppliers || [];
          setSuppliers(list);
          const qSupplierId = query.get('supplierId');
          if (qSupplierId && list.find((s) => s._id === qSupplierId)) setSupplierId(qSupplierId);
        } else {
          setSuppliers([]);
          setSupplierId('');
        }
      } catch (err) {
        console.error('Error fetching suppliers:', err);
        showModal('Error', 'Failed to fetch suppliers. Please try again later.');
      }
    };
    if (currentUser) initSuppliers();
  }, [currentUser, query]);

  // Fetch groups
  useEffect(() => {
    if (!supplierId) return;
    const qGroupId = query.get('groupId');
    const params = { supplierId };
    if (qGroupId) params.groupId = qGroupId;

    const doFetch = async () => {
      setSearching(true);
      setSearchError(null);
      try {
        await fetchGroups(params);
        setPortion('');
        setCalculatedPrice(0);
        setShowPayment(false);
      } catch (err) {
        console.error('Search error:', err);
        setSearchError('Failed to fetch groups');
        showModal('Error', 'Failed to fetch groups. Please try again later.');
      } finally {
        setSearching(false);
      }
    };
    doFetch();
  }, [supplierId, fetchGroups, query]);

  // Set selectedGroup from query param
  useEffect(() => {
    const qGroupId = query.get('groupId');
    if (qGroupId && searchResults.length > 0) {
      const found = searchResults.find((g) => g._id === qGroupId);
      setSelectedGroup(found || null);
    } else setSelectedGroup(null);
  }, [searchResults, query]);

  // Utility
  const deriveCostPerFull = (group) => group.costPerPortion ? Number(group.costPerPortion) : 0;
  const getPortionMultiplier = (p) => (p === 'Half' ? 0.5 : p === 'Quarter' ? 0.25 : 1);
  const computePrecisePortionsRemaining = (group, portionType = 'Full') => {
    const costPerFull = deriveCostPerFull(group);
    const rem = Number(group.remainingCost ?? 0);
    if (!costPerFull) return 0;
    return rem / (costPerFull * getPortionMultiplier(portionType));
  };
  const formatPortionFraction = (value) => {
    if (value <= 0) return '0';
    const whole = Math.floor(value);
    const fraction = value - whole;
    let fracLabel = '';
    if (fraction >= 0.75) fracLabel = '3/4';
    else if (fraction >= 0.5) fracLabel = '1/2';
    else if (fraction >= 0.25) fracLabel = '1/4';
    if (whole === 0 && fracLabel) return fracLabel;
    if (whole > 0 && fracLabel) return `${whole} ${fracLabel}`;
    return `${whole}`;
  };

  // Handlers
  const handleSearch = async () => {
    setSearching(true);
    setSearchError(null);
    try {
      const params = {};
      if (supplierId) params.supplierId = supplierId;
      if (groupName) params.groupName = groupName;
      await fetchGroups(params);
      clearSelection();
    } catch (err) {
      console.error('Search error:', err);
      setSearchError('Failed to search groups');
      showModal('Error', 'Failed to search groups. Please try again later.');
    } finally {
      setSearching(false);
    }
  };
  const clearSelection = () => {
    setSelectedGroup(null);
    setPortion('');
    setCalculatedPrice(0);
    setShowPayment(false);
  };
  const handleSelectGroup = (group) => {
    if (computePrecisePortionsRemaining(group, 'Full') <= EPSILON) return;
    setSelectedGroup(group);
    setPortion('');
    setCalculatedPrice(0);
    setShowPayment(false);
  };
  const handlePortionChange = (portionValue) => {
    setPortion(portionValue);
    if (!selectedGroup) return;
    setCalculatedPrice(deriveCostPerFull(selectedGroup) * getPortionMultiplier(portionValue));
  };
  const userId = localStorage.getItem('userId');
  const handlePayNow = () => {
    if (!portion) return showModal('Selection Required', 'Select a portion before paying.');
    setShowPayment(true);
  };
  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      if (!userId || !selectedGroup) {
        showModal('Error', 'No user or group selected.');
        return;
      }

      // IMPORTANT: prevent double execution
      if (paymentIntent?.status !== 'succeeded') return;

      const res = await api.post('/api/purchase-tracking', {
        groupId: selectedGroup._id,
        userId,
        portion,
        amountPaid: calculatedPrice,
        paymentIntentId: paymentIntent.id, // 👈 ADD THIS (important for debugging)
      });

      const updatedGroup = res.data?.updatedGroup;

      if (!updatedGroup) {
        throw new Error('Missing updatedGroup in response');
      }

      if (['full', 'closed'].includes(updatedGroup.status)) {
        setFadingGroupId(updatedGroup._id);

        setTimeout(() => {
          removeGroup(updatedGroup._id);
          clearSelection();
          setFadingGroupId(null);
        }, 800);
      } else {
        replaceGroup(updatedGroup);
        setSelectedGroup(updatedGroup);
      }

      showModal('Success', 'Payment successful and purchase recorded.');

    } catch (err) {
      console.error('Purchase error:', err);
      showModal('Error', err.message || 'Server error during purchase');
    } finally {
      setShowPayment(false);
      clearSelection();
      handleSearch();
    }
  };

  const visibleGroups = Array.isArray(searchResults)
    ? searchResults
      .filter(g => g.status !== 'closed')
      .filter(g => computePrecisePortionsRemaining(g, 'Full') > EPSILON)
      .filter(g => {
        if (currentUser?.role === 'member' || currentUser?.role === 'supplieradmin') {
          return g.supplierId?._id === currentUser.supplierId;
        }
        return true;
      })
    : [];

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h2 className="text-xl font-bold mb-4">Search Groups</h2>

      <div className="space-y-2 mb-4">
        <label className="font-medium">Select Supplier:</label>
        {scopedUser ? (
          <div className="border p-2 rounded w-full bg-gray-100 flex justify-between items-center">
            <span>{suppliers.find(s => s._id === currentUser.supplierId)?.name || 'No associated supplier'}</span>
            <input type="hidden" name="supplierId" value={currentUser.supplierId} />
          </div>
        ) : (
          <>
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="border p-2 w-full" disabled={suppliers.length < 1}>
              <option value="">-- Select Supplier --</option>
              {suppliers.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
            {suppliers.length < 1 && <p className="text-red-600 mt-1">No suppliers available.</p>}
          </>
        )}
        <input type="text" value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Group Name (optional)" className="border p-2 w-full" />
        <button onClick={handleSearch} className="bg-blue-600 text-white px-4 py-2 rounded" disabled={searching || suppliers.length < 1}>{searching ? 'Searching...' : 'Search'}</button>
        {searchError && <p className="text-red-500">{searchError}</p>}
      </div>

      {visibleGroups.length > 0 ? (
        <table className="w-full border-collapse border border-gray-300 mt-4">
          <thead>
            <tr className="bg-gray-200">
              <th className="border p-2">Group</th>
              <th className="border p-2">Supplier</th>
              <th className="border p-2">Animal</th>
              <th className="border p-2">Cost Per Full Portion</th>
              <th className="border p-2">Portions Remaining (Full)</th>
              <th className="border p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleGroups.map(group => {
              const fullPortionsLeft = computePrecisePortionsRemaining(group, 'Full');
              const isFull = fullPortionsLeft <= EPSILON;
              const fadeClass = group._id === fadingGroupId ? 'opacity-0 transition-opacity duration-700' : 'opacity-100';
              const rowClass = isFull ? 'bg-gray-100 text-gray-500' : 'bg-white';
              const costPerFull = deriveCostPerFull(group);

              return (
                <tr key={group._id} className={`text-center ${fadeClass} ${rowClass}`}>
                  <td className="border p-2">{group.name}</td>
                  <td className="border p-2">
                    {group.supplierId?.name ? (
                      <button type="button" onClick={() => openSupplierModal(group.supplierId)} className="text-blue-600 hover:underline focus:outline-none" title="View Supplier Details">{group.supplierId.name}</button>
                    ) : 'N/A'}
                  </td>
                  <td className="border p-2">{group.animal}</td>
                  <td className="border p-2">${costPerFull.toFixed(2)}</td>
                  <td className="border p-2">{formatPortionFraction(fullPortionsLeft)}</td>
                  <td className="border p-2">
                    {isFull ? (
                      <button disabled className="px-2 py-1 rounded bg-gray-300 text-white cursor-not-allowed">Full</button>
                    ) : (
                      <button onClick={() => handleSelectGroup(group)} disabled={selectedGroup?._id === group._id} className={`px-2 py-1 rounded ${selectedGroup?._id === group._id ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-green-500 text-white hover:bg-green-600'}`}>{selectedGroup?._id === group._id ? 'Selected' : 'Select'}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="text-center text-gray-600 mt-4">No groups found.</p>
      )}

      {selectedGroup && (
        <div className="mt-6">
          <h3 className="text-lg font-bold mb-2">Selected Group: {selectedGroup.name}</h3>
          <p className="mb-2">
            {portion ? (
              <>Portions Remaining ({portion}): {formatPortionFraction(computePrecisePortionsRemaining(selectedGroup, portion))}</>
            ) : (
              <>Portions Remaining (Full): {formatPortionFraction(computePrecisePortionsRemaining(selectedGroup, 'Full'))}</>
            )}
          </p>

          <label className="block mb-2">Select Portion:</label>
          <PortionSelector group={selectedGroup} portion={portion} onChange={handlePortionChange} />

          {portion && (
            <div className="mb-4">
              <p>You selected: <strong>{portion}</strong></p>
              <p>Amount to Pay: <strong>${calculatedPrice.toFixed(2)}</strong></p>
            </div>
          )}

          {!showPayment && (
            <div className="flex gap-4">
              <button onClick={handlePayNow} className="bg-green-600 text-white px-4 py-2 rounded" disabled={!portion}>Proceed to Pay</button>
              <button onClick={handlePaymentSuccess} className="bg-gray-500 text-white px-4 py-2 rounded">Clear Selection</button>
            </div>
          )}
        </div>
      )}

      {showPayment && selectedGroup && (
        <div className="mt-6">
          <h3 className="text-lg font-bold mb-2">Payment for {selectedGroup.name} - {portion}</h3>
          <StripePayment amount={calculatedPrice} onSuccess={handlePaymentSuccess} />
        </div>
      )}

      <InfoModal isOpen={modalInfo.isOpen} onClose={closeModal} title={modalInfo.title}>
        {modalInfo.content || <p>No additional information.</p>}
      </InfoModal>

      <SupplierInfoModal isOpen={supplierModalOpen} onClose={closeSupplierModal} supplier={supplierForModal} />
    </div>
  );
}
